import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Plane, Building2, FileText, MailCheck, Inbox } from "lucide-react";

const cards = [
  { key: "trips", label: "Itinerarios", icon: Plane, to: "/itinerarios", color: "blue" },
  { key: "pending_bookings", label: "Reservas pendientes", icon: Inbox, to: "/itinerarios", color: "orange" },
  { key: "providers", label: "Proveedores", icon: Building2, to: "/proveedores", color: "slate" },
  { key: "templates", label: "Plantillas", icon: FileText, to: "/plantillas", color: "slate" },
  { key: "emails_sent", label: "Correos enviados", icon: MailCheck, to: "/correos", color: "green" },
];

const colorMap = {
  blue: "text-blue-700 bg-blue-50",
  orange: "text-orange-700 bg-orange-50",
  slate: "text-gray-700 bg-gray-100",
  green: "text-emerald-700 bg-emerald-50",
};

export default function DashboardPage() {
  const [stats, setStats] = useState({});
  const [trips, setTrips] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, t] = await Promise.all([api.get("/stats"), api.get("/trips")]);
      setStats(s.data);
      setTrips(t.data.slice(0, 6));
    })();
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div>
        <p className="vv-label">Panel</p>
        <h1 className="text-3xl font-semibold tracking-tight mt-1">Resumen de operaciones</h1>
        <p className="text-sm text-gray-500 mt-1">Estado general de itinerarios, proveedores y correos.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {cards.map((c) => (
          <Link
            key={c.key}
            to={c.to}
            className="vv-card p-4 hover:shadow-sm transition-shadow"
            data-testid={`stat-${c.key}`}
          >
            <div className={`w-9 h-9 rounded-md flex items-center justify-center ${colorMap[c.color]}`}>
              <c.icon size={18} strokeWidth={1.8} />
            </div>
            <div className="text-2xl font-semibold mt-3 tracking-tight">{stats[c.key] ?? "—"}</div>
            <div className="text-xs text-gray-500 mt-0.5">{c.label}</div>
          </Link>
        ))}
      </div>

      <div className="vv-card overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--vv-border)]">
          <div>
            <h3 className="text-sm font-semibold">Últimos itinerarios</h3>
            <p className="text-xs text-gray-500">Acceso rápido a los viajes más recientes</p>
          </div>
          <Link to="/itinerarios" className="text-xs text-blue-600 hover:underline">Ver todos →</Link>
        </div>
        {trips.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            Aún no hay itinerarios. <Link className="text-blue-600 hover:underline" to="/itinerarios">Crear el primero</Link>.
          </div>
        ) : (
          <table className="vv-table w-full">
            <thead>
              <tr>
                <th>Ref</th>
                <th>Cliente</th>
                <th>Llegada</th>
                <th>Salida</th>
                <th>Pax</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {trips.map((t) => (
                <tr key={t.id}>
                  <td className="mono text-xs">{t.reference}</td>
                  <td className="font-medium">{t.client_name}</td>
                  <td className="text-sm text-gray-600">{t.arrival_date || "—"}</td>
                  <td className="text-sm text-gray-600">{t.departure_date || "—"}</td>
                  <td className="text-sm">{t.adults}A {t.children ? `+${t.children}N` : ""}</td>
                  <td><span className="text-[10px] uppercase tracking-wider text-gray-500">{t.status}</span></td>
                  <td className="text-right">
                    <Link to={`/itinerarios/${t.id}`} className="text-xs text-blue-600 hover:underline">Abrir</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
